// src/pages/VerificarPage.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Search, FileText, User, Calendar, DollarSign, Loader2 } from 'lucide-react';
import api from '../services/api';

interface DocumentoVerificado {
  numero: string;
  fecha: string;
  total: number;
  estado: string;
  cliente: string;
  vendedor: string;
  codigo_verificacion: string;
}

interface VerificacionResponse {
  valido: boolean;
  mensaje: string;
  codigo?: string;
  documento?: DocumentoVerificado;
}

export default function VerificarPage() {
  const { codigo: codigoParam } = useParams<{ codigo?: string }>();
  const navigate = useNavigate();
  
  const [codigo, setCodigo] = useState(codigoParam || '');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<VerificacionResponse | null>(null);
  const [verificado, setVerificado] = useState(false);

  // Verificar automáticamente si viene código en la URL
  useEffect(() => {
    if (codigoParam) {
      verificarCodigo(codigoParam);
    }
  }, [codigoParam]);

  const verificarCodigo = async (codigoAVerificar: string) => {
    if (!codigoAVerificar.trim()) return;
    
    setLoading(true);
    setVerificado(false);
    setResultado(null);
    
    try {
      const response = await api.get<VerificacionResponse>(`/notas-venta/verificar/${codigoAVerificar.toUpperCase()}`);
      setResultado(response.data);
      setVerificado(true);
    } catch (error: any) {
      setResultado({
        valido: false,
        mensaje: error.response?.data?.mensaje || 'Error al verificar el código',
        codigo: codigoAVerificar
      });
      setVerificado(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verificarCodigo(codigo);
    // Actualizar URL sin recargar
    navigate(`/verificar/${codigo.toUpperCase()}`, { replace: true });
  };

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-EC', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-sm border-b border-white/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-blue-500 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">BIO'DACAR</h1>
                <p className="text-xs text-blue-300">Sistema de Verificación de Documentos</p>
              </div>
            </div>
            <a 
              href="/login" 
              className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
            >
              Ir al Sistema →
            </a>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Título */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-full mb-4">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Verificación de Documentos
            </h2>
            <p className="text-slate-400">
              Ingrese el código de verificación para validar la autenticidad del documento
            </p>
          </div>

          {/* Formulario de búsqueda */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
            <form onSubmit={handleSubmit} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                  placeholder="Ingrese código (ej: AB12CD34)"
                  className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-lg tracking-widest"
                  maxLength={8}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !codigo.trim()}
                className="px-8 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    Verificar
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Resultado de verificación */}
          {verificado && resultado && (
            <div className={`rounded-2xl border-2 overflow-hidden transition-all duration-500 ${
              resultado.valido 
                ? 'bg-emerald-500/10 border-emerald-500/50' 
                : 'bg-red-500/10 border-red-500/50'
            }`}>
              {/* Header del resultado */}
              <div className={`px-6 py-4 ${
                resultado.valido ? 'bg-emerald-500/20' : 'bg-red-500/20'
              }`}>
                <div className="flex items-center gap-3">
                  {resultado.valido ? (
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-400" />
                  )}
                  <div>
                    <h3 className={`text-xl font-bold ${
                      resultado.valido ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {resultado.valido ? '✓ DOCUMENTO AUTÉNTICO' : '✗ DOCUMENTO NO VÁLIDO'}
                    </h3>
                    <p className="text-sm text-slate-300">{resultado.mensaje}</p>
                  </div>
                </div>
              </div>

              {/* Detalles del documento */}
              {resultado.valido && resultado.documento && (
                <div className="p-6">
                  <div className="grid gap-4">
                    <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                      <FileText className="w-6 h-6 text-blue-400" />
                      <div>
                        <p className="text-xs text-slate-400 uppercase">Número de Documento</p>
                        <p className="text-lg font-bold text-white">{resultado.documento.numero}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                        <Calendar className="w-6 h-6 text-purple-400" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase">Fecha de Emisión</p>
                          <p className="text-white font-semibold">{formatFecha(resultado.documento.fecha)}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                        <DollarSign className="w-6 h-6 text-green-400" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase">Total</p>
                          <p className="text-white font-bold text-lg">${resultado.documento.total.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                        <User className="w-6 h-6 text-cyan-400" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase">Cliente</p>
                          <p className="text-white font-semibold">{resultado.documento.cliente}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                        <User className="w-6 h-6 text-amber-400" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase">Vendedor</p>
                          <p className="text-white font-semibold">{resultado.documento.vendedor}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
                      <div>
                        <p className="text-xs text-slate-400 uppercase">Estado del Documento</p>
                        <p className={`font-bold ${
                          resultado.documento.estado === 'ACTIVA' ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {resultado.documento.estado === 'ACTIVA' ? '✓ ACTIVO' : '✗ ANULADO'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400 uppercase">Código Verificado</p>
                        <p className="font-mono text-lg font-bold text-blue-400">
                          {resultado.documento.codigo_verificacion}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Si no es válido */}
              {!resultado.valido && (
                <div className="p-6">
                  <div className="text-center text-slate-400">
                    <p className="mb-4">El código <span className="font-mono text-red-400">{resultado.codigo}</span> no corresponde a ningún documento registrado en el sistema.</p>
                    <p className="text-sm">
                      Si cree que esto es un error, verifique que el código esté escrito correctamente 
                      o contacte al emisor del documento.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Información adicional */}
          <div className="mt-8 text-center text-sm text-slate-500">
            <p className="mb-2">🔒 Sistema de verificación seguro de BIO'DACAR</p>
            <p>
              Los documentos generados por BIO'DACAR contienen un código único de verificación 
              que permite comprobar su autenticidad.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© 2025 BIO'DACAR - Sistema de Gestión de Inventario y Ventas</p>
        </div>
      </footer>
    </div>
  );
}
